import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "reactstrap";
import { Button, Icon } from "../../../components/Component";
import { InputNumber, Modal, Select, Table, Tag, Tooltip } from "antd";
import { machineryApis } from "../../../api/MachineryNorms";
import { useWbsName } from "../shared/useWbsCodes";
import {
  MACHINERY_HEADS,
  LIVE_MACHINERY_HEADS,
} from "../machinery-norms/machineryHeads";

// Applies MACHINERY norms to a task as a decoupled machinery REQUIREMENT.
// Each selected norm records one row: appliedQuantity = task work quantity (or
// an override) — there is NO factor — priced at the norm's rate. Unlike the
// manpower flow the option list is NOT the org dropdown: only the norms of the
// task's own project + activity are applicable (GET task/:id/applicable).
// qty 0 is valid; negatives are blocked. Per-norm skips (already applied /
// inactive) come back in `skipped[]`.

export interface ApplyMachineryTaskInfo {
  taskId: number;
  taskName?: string;
  workType?: string;
  workQuantity?: number | string | null;
  outputQuantity?: number | string | null;
}

interface MachineryOption {
  machineryNormId: number;
  category?: string;
  normCode: string;
  description?: string | null;
  activityName?: string;
  nearestDump?: string | null;
  totalLead?: string | number | null;
  rate?: string | number | null;
  uomId?: number | null;
  uomName?: string;
}

interface SkippedItem {
  machineryNormId?: number;
  normCode?: string;
  error?: string;
}

interface ApplyResult {
  applied: number;
  skipped: SkippedItem[];
}

type Stage = "form" | "done";

const round2 = (n: number) => Math.round(n * 100) / 100;

const fmtRate = (n: number) =>
  `₹${(Number.isFinite(n) ? n : 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Head colours for the small category tag on each option / preview row.
const headColor = (category?: string) => {
  const c = (category || "").toUpperCase();
  if (c.includes("MUCK")) return "geekblue";
  if (c.includes("DRILL")) return "purple";
  if (c.includes("TRANS")) return "cyan";
  return "blue";
};

const ApplyMachineryNormModal = ({
  open,
  onClose,
  task,
  onApplied,
  replaceMode = false,
  replaceMachineryNormIds,
  initialHead,
}: {
  open: boolean;
  onClose: () => void;
  task: ApplyMachineryTaskInfo;
  onApplied?: () => void;
  // Re-apply ("swap on apply"): preselect the current set; on submit remove all
  // current items first, then apply the chosen set. Cancelling leaves them intact.
  replaceMode?: boolean;
  replaceMachineryNormIds?: number[];
  // Open the head dropdown focused on this head (per-head Re-apply / Apply).
  initialHead?: string;
}) => {
  const wbsName = useWbsName();
  const [stage, setStage] = useState<Stage>("form");
  const [options, setOptions] = useState<MachineryOption[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  // Applicability context from the backend: the task's activity + work basis.
  const [activityName, setActivityName] = useState<string>("");
  const [applicableWorkQty, setApplicableWorkQty] = useState<number | null>(null);
  const [workUom, setWorkUom] = useState<string>("");
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // Head (category) picker above the dropdown — the dropdown only lists the
  // chosen head's norms. Selections persist across head switches; the preview
  // always shows every selected norm.
  const [headFilter, setHeadFilter] = useState<string | null>(null);
  const [overrideQty, setOverrideQty] = useState<number | null>(null);
  const [itemsError, setItemsError] = useState("");
  const [qtyError, setQtyError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<ApplyResult | null>(null);

  // Basis quantity: backend-provided task work quantity (authoritative),
  // falling back to the value already on the task card. Decimals arrive as strings.
  const workQuantity =
    applicableWorkQty != null ? applicableWorkQty : Number(task?.workQuantity) || 0;
  // The basis: override (0 allowed) if entered, else the task work quantity.
  const basisQty = overrideQty != null ? Number(overrideQty) : workQuantity;

  const resetAll = useCallback(() => {
    setStage("form");
    setSelectedIds([]);
    setHeadFilter(null);
    setOverrideQty(null);
    setItemsError("");
    setQtyError("");
    setSubmitting(false);
    setError("");
    setResult(null);
  }, []);

  const loadItems = useCallback(async () => {
    const taskId = Number(task?.taskId);
    if (!taskId) return;
    try {
      setLoadingItems(true);
      const res = await machineryApis.getApplicableNorms(taskId);
      if (res?.status === true) {
        const data = res?.data || {};
        const list: any[] = Array.isArray(data.items) ? data.items : [];
        setActivityName(data.activityName || "");
        setApplicableWorkQty(
          data.workQuantity != null && data.workQuantity !== ""
            ? Number(data.workQuantity)
            : null,
        );
        setWorkUom(data.workUom || "");
        setOptions(
          list.map((n: any) => ({
            machineryNormId: n.machineryNormId,
            category: n.category,
            normCode: n.normCode,
            description: n.description,
            activityName: n.activityName,
            nearestDump: n.nearestDump,
            totalLead: n.totalLead,
            rate: n.rate,
            uomId: n.uomId,
            uomName: n.uomName,
          })),
        );
      } else {
        setError(res?.message || "Failed to load the applicable machinery norms");
      }
    } catch {
      setError("Failed to load the applicable machinery norms");
    } finally {
      setLoadingItems(false);
    }
  }, [task?.taskId]);

  useEffect(() => {
    if (open) {
      resetAll();
      loadItems();
      // Open focused on the requested head (per-head Re-apply / Apply).
      if (initialHead) setHeadFilter(initialHead);
      // Re-apply: preselect the currently applied set (of this head).
      if (replaceMode && replaceMachineryNormIds?.length) {
        setSelectedIds(replaceMachineryNormIds.map((id) => Number(id)));
      }
    }
  }, [
    open,
    resetAll,
    loadItems,
    replaceMode,
    replaceMachineryNormIds,
    initialHead,
  ]);

  const optionMap = useMemo(() => {
    const m = new Map<number, MachineryOption>();
    options.forEach((o) => m.set(o.machineryNormId, o));
    return m;
  }, [options]);

  // Applicable-norm count per head (0 for a head with none on this task).
  const headCounts = useMemo(() => {
    const m = new Map<string, number>();
    options.forEach((o) => {
      const k = (o.category || "MACHINERY").toUpperCase();
      m.set(k, (m.get(k) || 0) + 1);
    });
    return m;
  }, [options]);

  // The head dropdown lists ALL live heads (so heads with no norms for this
  // task still show, with a (0) count), plus any head that has data but isn't
  // in the live list (defensive — shouldn't normally happen).
  const headKeys = useMemo(() => {
    const live = LIVE_MACHINERY_HEADS.map((h) => h.key);
    const extra = Array.from(headCounts.keys()).filter(
      (k) => !live.includes(k),
    );
    return [...live, ...extra];
  }, [headCounts]);

  // Active head: the picked one (if valid), else the first head that actually
  // has norms for this task, else the first head in the list.
  const activeHead =
    headFilter && headKeys.includes(headFilter)
      ? headFilter
      : headKeys.find((k) => (headCounts.get(k) || 0) > 0) ??
        headKeys[0] ??
        null;

  const headLabel = useCallback((key: string) => {
    const known = MACHINERY_HEADS.find((h) => h.key === key);
    return known?.label ?? key.charAt(0) + key.slice(1).toLowerCase();
  }, []);

  const toOption = useCallback(
    (o: MachineryOption) => ({
      value: o.machineryNormId,
      label: o.description ? `${o.normCode} — ${o.description}` : o.normCode,
    }),
    [],
  );

  // Dropdown lists ONLY the active head's norms (flat — the head picker
  // replaces the old in-dropdown group headers).
  const selectOptions = useMemo(
    () =>
      options
        .filter((o) => (o.category || "MACHINERY").toUpperCase() === activeHead)
        .map(toOption),
    [options, activeHead, toOption],
  );

  // Rich option row: «code — description» + head tag + rate suffix.
  const renderOption = useCallback(
    (option: any) => {
      const o = optionMap.get(Number(option.value));
      if (!o) return option.label;
      const rate = Number(o.rate) || 0;
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            minWidth: 0,
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {option.label}
          </span>
          {o.category && (
            <Tag
              color={headColor(o.category)}
              style={{ margin: 0, fontSize: 11, lineHeight: "18px" }}
            >
              {o.category}
            </Tag>
          )}
          <span style={{ color: "#8094ae", fontSize: 12, whiteSpace: "nowrap" }}>
            {fmtRate(rate)}
            {o.uomName ? ` / ${o.uomName}` : ""}
          </span>
        </div>
      );
    },
    [optionMap],
  );

  // Live preview: qty = round2(override ?? workQuantity), amount = qty × rate.
  const previewRows = useMemo(
    () =>
      selectedIds.map((id) => {
        const o = optionMap.get(id);
        const rate = Number(o?.rate) || 0;
        const qty = round2(basisQty);
        return {
          key: id,
          normCode: o?.normCode || `#${id}`,
          description: o?.description || "",
          category: o?.category || "",
          uomName: o?.uomName || "",
          nearestDump: o?.nearestDump || "",
          totalLead: o?.totalLead,
          qty,
          rate,
          amount: round2(qty * rate),
        };
      }),
    [selectedIds, optionMap, basisQty],
  );

  const totalAmount = useMemo(
    () => round2(previewRows.reduce((sum, r) => sum + (r.amount || 0), 0)),
    [previewRows],
  );

  const validate = () => {
    let ok = true;
    if (!selectedIds.length) {
      setItemsError("Please select at least one machinery norm");
      ok = false;
    } else {
      setItemsError("");
    }
    // 0 is allowed; only negatives are rejected.
    if (overrideQty != null && Number(overrideQty) < 0) {
      setQtyError("Override quantity cannot be negative");
      ok = false;
    } else {
      setQtyError("");
    }
    return ok;
  };

  const handleApply = async () => {
    if (!validate()) return;
    setError("");
    try {
      setSubmitting(true);
      // Re-apply (swap on apply): remove the current set first so the new
      // selection replaces it. Done here (not on button click) so cancelling
      // the modal leaves the current set intact.
      if (replaceMode && replaceMachineryNormIds?.length) {
        for (const id of replaceMachineryNormIds) {
          const rm = await machineryApis.removeFromTask({
            taskId: Number(task.taskId),
            machineryNormId: Number(id),
            reason: "Re-applied",
          });
          if (rm?.status !== true) {
            setError(
              rm?.data?.message || rm?.message || "Failed to remove the current machinery",
            );
            setSubmitting(false);
            return;
          }
        }
      }
      const res = await machineryApis.applyToTask({
        taskId: Number(task.taskId),
        machineryNormIds: selectedIds.map((id) => Number(id)),
        ...(overrideQty != null ? { overrideQty: Number(overrideQty) } : {}),
      });

      if (res?.status === true) {
        const data = res?.data || {};
        setResult({
          applied: Number(data.applied) || 0,
          skipped: Array.isArray(data.skipped) ? data.skipped : [],
        });
        setStage("done");
        return;
      }

      const rawMessage = res?.data?.message ?? res?.message;
      const message = Array.isArray(rawMessage)
        ? rawMessage.join("; ")
        : rawMessage || "Failed to apply the machinery norm. Please try again.";
      setError(message);
    } catch (err: any) {
      setError(err?.message || "Something went wrong while applying the machinery norm");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    onApplied?.();
    onClose();
  };

  const previewColumns = [
    {
      title: "Norm",
      dataIndex: "normCode",
      key: "normCode",
      ellipsis: true,
      render: (_: any, r: any) => {
        const leadDump = [
          r.totalLead != null && r.totalLead !== ""
            ? `Lead: ${r.totalLead}`
            : "",
          r.nearestDump ? `Dump: ${r.nearestDump}` : "",
        ]
          .filter(Boolean)
          .join(" · ");
        return (
          <Tooltip title={leadDump || undefined}>
            <span style={{ fontWeight: 500 }}>{r.normCode}</span>
            {r.category && (
              <Tag
                color={headColor(r.category)}
                style={{ marginLeft: 6, fontSize: 11, lineHeight: "18px" }}
              >
                {r.category}
              </Tag>
            )}
            {(r.description || leadDump) && (
              <div
                style={{
                  color: "#9ca3af",
                  fontSize: 11,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {r.description}
                {r.description && leadDump ? " · " : ""}
                {leadDump}
              </div>
            )}
          </Tooltip>
        );
      },
    },
    { title: "UOM", dataIndex: "uomName", key: "uomName", width: 80 },
    { title: "Qty", dataIndex: "qty", key: "qty", width: 80 },
    { title: "Rate", dataIndex: "rate", key: "rate", width: 90 },
    { title: "Amount", dataIndex: "amount", key: "amount", width: 100 },
  ];

  const noApplicable = !loadingItems && options.length === 0;

  return (
    <Modal
      title={`${replaceMode ? "Re-apply Machinery Norm" : "Apply Machinery Norm"}${
        task?.taskName ? ` — ${task.taskName}` : ""
      }`}
      open={open}
      footer={null}
      onCancel={stage === "done" ? handleDone : onClose}
      maskClosable={false}
      width={640}
    >
      {stage === "form" && (
        <div>
          <p className="mb-2">
            Applying machinery norms records each selected norm as a machinery
            requirement on this task: <em>quantity = task work quantity (or the
            override)</em>, priced at the norm's rate — no factor is applied. A
            norm can be applied to a task only once — to re-apply, remove it
            (with a reason) first.
          </p>
          <p className="mb-3" style={{ fontSize: 13 }}>
            <Tag color="blue">
              {activityName ? `Activity: ${wbsName(activityName)} · ` : ""}
              Basis: {basisQty}
              {workUom ? ` ${workUom}` : ""}
            </Tag>
          </p>

          {/* With a single head there is nothing to pick — the dropdown would
              show one option that cannot be changed. */}
          {headKeys.length > 1 && (
            <div className="form-group mb-2">
              <label className="form-label">Head</label>
              <div>
                <Select
                  value={activeHead ?? undefined}
                  onChange={(value) => setHeadFilter(String(value))}
                  disabled={submitting}
                  style={{ width: 280, maxWidth: "100%" }}
                  options={headKeys.map((key) => {
                    const count = headCounts.get(key) || 0;
                    return {
                      value: key,
                      label: `${headLabel(key)} (${count})`,
                    };
                  })}
                />
              </div>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">
              Machinery Norms <span style={{ color: "red" }}>*</span>
            </label>
            <Select
              style={{ width: "100%" }}
              mode="multiple"
              placeholder="Select one or more machinery norms"
              showSearch
              optionFilterProp="label"
              loading={loadingItems}
              options={selectOptions as any}
              optionRender={renderOption}
              value={selectedIds}
              onChange={(value) => {
                setSelectedIds(value as number[]);
                setItemsError("");
              }}
              disabled={submitting || noApplicable}
              status={itemsError ? "error" : undefined}
            />
            {itemsError && (
              <span className="invalid" style={{ color: "#e85347", fontSize: 12 }}>
                {itemsError}
              </span>
            )}
            {noApplicable && (
              <Alert
                color="light"
                className="py-2 mt-2 mb-0"
                isOpen
                style={{ textAlign: "left", fontSize: "13px" }}
              >
                No machinery norms linked to this task's activity — add or link
                them in <strong>Norms → Machinery</strong> (include the activity
                code like <code>(AE-0102)</code> in uploads).
              </Alert>
            )}
          </div>

          <div className="form-group mt-2">
            <label className="form-label">
              Override Quantity{" "}
              <span className="text-soft" style={{ fontWeight: 400 }}>
                (optional — replaces the task work quantity; 0 allowed)
              </span>
            </label>
            <InputNumber
              style={{ width: "100%" }}
              placeholder={`Defaults to task work quantity (${workQuantity})`}
              value={overrideQty}
              onChange={(value) => {
                setOverrideQty(value as number | null);
                setQtyError("");
              }}
              disabled={submitting || noApplicable}
              status={qtyError ? "error" : undefined}
            />
            {qtyError && (
              <span className="invalid" style={{ color: "#e85347", fontSize: 12 }}>
                {qtyError}
              </span>
            )}
          </div>

          {previewRows.length > 0 && (
            <div className="mt-3">
              <label className="form-label">Preview</label>
              <Table
                size="small"
                pagination={false}
                columns={previewColumns as any}
                dataSource={previewRows}
                scroll={{ y: 220 }}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={4}>
                      <strong>Machinery Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>
                      <strong>{totalAmount}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </div>
          )}

          <Alert
            isOpen={!!error}
            color="danger"
            className="py-1 mt-3 mb-0"
            style={{ textAlign: "left", fontSize: "14px" }}
          >
            {error}
          </Alert>

          <div className="d-flex mt-4" style={{ gap: 12 }}>
            {submitting ? (
              <Button color="primary" type="button">
                <Spinner size="sm" color="light" />
              </Button>
            ) : (
              <Button color="primary" onClick={handleApply} disabled={noApplicable}>
                <Icon name="check" />
                <span>Apply Machinery Norm</span>
              </Button>
            )}
            {!submitting && (
              <Button
                color="primary"
                className="btn-dim btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}

      {stage === "done" && result && (
        <div>
          <div className="text-center py-2">
            <Icon name="check-circle" style={{ fontSize: 42, color: "#1ee0ac" }} />
            <h6 className="mt-2">{result.applied} machinery norm(s) applied</h6>
          </div>
          {result.skipped.length > 0 && (
            <Alert
              color="warning"
              className="py-2 mt-2 mb-0"
              style={{ textAlign: "left", fontSize: "14px" }}
            >
              <strong>Skipped norms ({result.skipped.length})</strong>
              <ul className="mt-1 mb-0" style={{ paddingLeft: 18 }}>
                {result.skipped.map((skip, index) => (
                  <li key={index}>
                    <strong>{skip.normCode || `Norm #${skip.machineryNormId}`}</strong>
                    {skip.error ? ` — ${skip.error}` : ""}
                  </li>
                ))}
              </ul>
            </Alert>
          )}
          <div className="d-flex justify-content-center mt-3">
            <Button color="primary" onClick={handleDone}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ApplyMachineryNormModal;
